'use client'

import Link from 'next/link'
import { ListingCard } from '@/components/marketing/content/listing-card'
import { ListingGrid } from '@/components/marketing/content/listing-grid'
import { ListingPagination } from '@/components/marketing/content/listing-pagination'
import { BeforeAfterCover } from '@/components/marketing/content/before-after-cover'
import { track } from '@/lib/analytics/track'
import { buildListingHref, LISTING_PAGE_SIZE } from '@/lib/marketing/pagination'

interface ModelCard {
  slug: string
  title: string
  description: string
  category: string
  coverImage?: string
  href: string
}

const BASE_PATH = '/models'

const beforeAfterSlots: Record<
  string,
  { before: string; after: string; alt: string }
> = {
  // Same H3 frame before and after a single relight instruction — the split
  // view lands because nothing but the light actually moved.
  'minimax-h3': {
    before:
      'https://cdn.soku.ai/marketing/images/tools/minimax-h3/cover-before.jpg',
    after:
      'https://cdn.soku.ai/marketing/images/tools/minimax-h3/cover-after.jpg',
    alt: 'MiniMax H3 relighting a clip',
  },
}

export function ModelsListing({
  models,
  categories,
  activeCategory,
  page,
  totalPages,
}: {
  models: ModelCard[]
  categories: string[]
  activeCategory: string
  page: number
  totalPages: number
}) {
  const pills = [
    { id: 'all', label: 'All' },
    ...categories.map(c => ({ id: c, label: c })),
  ]

  return (
    <>
      {/* Category pills — real ?category= links; selecting one resets to page 1 */}
      <div className="mb-8 flex flex-wrap gap-3">
        {pills.map(cat => {
          const isActive = cat.id === activeCategory
          return (
            <Link
              key={cat.id}
              href={buildListingHref(BASE_PATH, { category: cat.id })}
              scroll={false}
              onClick={() => track('models_filter', { category: cat.id })}
              className={`rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'border-foreground bg-foreground text-background shadow-md'
                  : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground'
              }`}
            >
              {cat.label}
            </Link>
          )
        })}
      </div>

      <ListingGrid>
        {models.map((model, index) => {
          const ba = beforeAfterSlots[model.slug]
          return (
            <ListingCard
              key={model.slug}
              href={model.href}
              title={model.title}
              description={model.description}
              category={model.category}
              image={model.coverImage}
              imageSlot={
                ba ? (
                  <BeforeAfterCover
                    before={ba.before}
                    after={ba.after}
                    alt={ba.alt}
                  />
                ) : undefined
              }
              onClick={() =>
                track('models_card_click', {
                  slug: model.slug,
                  position: (page - 1) * LISTING_PAGE_SIZE + index + 1,
                })
              }
            />
          )
        })}
      </ListingGrid>

      <ListingPagination
        basePath={BASE_PATH}
        page={page}
        totalPages={totalPages}
        category={activeCategory}
      />
    </>
  )
}
